// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import Page from "@insite/mobius/Page";
import loadOpenBidDetails from "@steiner/App/Handlers/OpenBids/LoadOpenBidDetails";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ OrderNumber?: string; GenerationId?: string }>(search);
    const orderNumber = parsedQuery.OrderNumber;
    const generationId = parsedQuery.GenerationId;

    return {
        orderNumber,
        generationId,
        openBidDetails: state.xOpenBidDetails.dataView,
        isLoading: state.xOpenBidDetails.isLoading,
    };
};

const mapDispatchToProps = {
    loadOpenBidDetails,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OpenBidsDetailsPage = ({
    id,
    orderNumber,
    generationId,
    isLoading,
    openBidDetails,
    loadOpenBidDetails,
}: Props) => {
    const isSameOrderNumber = (orderNumber: string, openBidDetails: any) => {
        return openBidDetails?.salesOrder?.orderHeader?.orderId === orderNumber;
    };

    useEffect(() => {
        if (orderNumber && !isLoading && !isSameOrderNumber(orderNumber, openBidDetails)) {
            loadOpenBidDetails({ orderNumber, generationId });
        }
    }, [orderNumber, isLoading, openBidDetails]);

    return (
        <Page data-test-selector="openBidsDetails">
            <Zone contentId={id} zoneName="Content"></Zone>
            <AddToListModal />
        </Page>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenBidsDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "OpenBidsDetailsPage" instead of this constant.
 */
export const OpenBidsDetailsPageContext = "OpenBidsDetailsPage";
